<template>
  <!-- Zweite Info-/Service-Seite: installiert die benoetigten OTT- und
       Cast-Apps und zeigt pro App, was daraus geworden ist.
       Erreichbar mit der GELBEN Taste auf der Info-Seite (siehe
       pages/Info.vue) - dorthin fuehrt aus dem Hauptmenue die Tastenfolge
       ROT-GRUEN-GELB-BLAU-ROT. -->
  <div class="app-install" ref="scrollContainer">
    <h2>
      App-Installation
      <span class="badge">{{ platformLabel }}</span>
      <span class="badge" v-if="lang">Sprache: {{ lang }}</span>
    </h2>

    <div class="hint">
      <template v-if="running">
        Installiere {{ done }} von {{ total }} …
      </template>
      <template v-else-if="finished">
        Fertig: {{ countInstalled }} installiert, {{ countFailed }} fehlgeschlagen,
        {{ countSkipped }} uebersprungen.
        <br />GRUEN = erneut versuchen, ROT/ZURUECK = zurueck zur Info-Seite.
      </template>
      <template v-else-if="loading">
        Ermittle Zustand der Apps …
      </template>
      <template v-else>
        {{ summary }}
        <br />GELB = Installation starten, GRUEN = Zustand neu einlesen,
        ROT/ZURUECK = zurueck zur Info-Seite.
      </template>
    </div>

    <!-- Geraete-Diagnose: erklaert, warum die Tabelle so aussieht, wie sie
         aussieht. Ohne diesen Block bleibt ein "keine Auskunft" fuer alle Apps
         unerklaerlich (fehlende webapis, nicht initialisierter SI-Token,
         fehlendes application.info-Privileg, ...). -->
    <div class="diag" v-if="diagLines.length">
      <div class="diag-title">Geraete-Diagnose</div>
      <div class="diag-line" v-for="line in diagLines" :key="line.label" :class="'diag-' + line.level">
        <span class="diag-label">{{ line.label }}</span>
        <span class="diag-value">{{ line.value }}</span>
      </div>
      <div class="diag-note" v-if="blockingHint">{{ blockingHint }}</div>
    </div>

    <table v-if="rows.length">
      <tr>
        <th class="col-name">App</th>
        <th class="col-id">App-ID</th>
        <th class="col-state">Zustand</th>
        <th class="col-detail">Detail</th>
      </tr>
      <tr class="row" v-for="row in rows" :key="row.group + row.key">
        <td class="col-name">
          {{ row.name }}<span class="group"> · {{ row.group }}</span><span
            v-if="row.lang"
            class="lang"
          > · {{ row.lang }}</span>
        </td>
        <td class="col-id">
          {{ row.appId || '(über den Namen)' }}<span
            v-if="row.resolvedAppId && row.resolvedAppId !== row.appId"
            class="resolved"
          ><br />→ am Gerät: {{ row.resolvedAppId }}</span>
          <!-- Alle passenden Einträge am Gerät. Nur interessant, wenn es mehr
               als einen gibt: dann führt der TV mehrere Varianten derselben
               App - der einzige Weg herauszufinden, ob es überhaupt
               sprachspezifische App-Ids gibt. -->
          <span v-if="row.deviceMatches && row.deviceMatches.length > 1" class="variants">
            <br />{{ row.deviceMatches.length }} Varianten am Gerät:
            <span v-for="m in row.deviceMatches" :key="m.id">
              <br />&nbsp;&nbsp;{{ m.id }}<template v-if="m.name"> — {{ m.name }}</template><template v-if="m.variant"> (Nebenvariante)</template>
            </span>
          </span>
        </td>
        <td class="col-state" :class="'state-' + row.state">{{ stateLabel(row) }}</td>
        <td class="col-detail">{{ row.detail }}</td>
      </tr>
    </table>
    <div class="hint" v-else-if="!loading">
      Keine App-Ziele konfiguriert - fuer die aktive Plattform ({{ platformLabel }})
      ist in keiner Cast-App eine App-Id hinterlegt.
    </div>
  </div>
</template>

<script>
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import { NAV_TYPE } from '@/store/modules/navigation';
import Cast from '@/helpers/Cast';

const STATE_LABEL = {
  installed: 'installiert',
  installable: 'nicht installiert',
  blocked: 'gesperrt',
  missing: 'unbekannt am Gerät',
  unknown: 'keine Auskunft',
};

export default {
  mixins: [keyHandler],
  data() {
    return {
      rows: [],
      loading: false,
      running: false,
      finished: false,
      done: 0,
      total: 0,
      // Rohwerte der Geraete-Erkennung (Cast.capabilities()) - Grundlage der
      // Diagnose-Zeilen.
      caps: null,
    };
  },
  computed: {
    platformLabel() {
      const p = Cast.platform;
      if (p === 'lg') return 'LG WebOS';
      if (p === 'tizen') return 'Samsung Tizen';
      return 'ohne Geräte-API';
    },
    // Aktive Geraetesprache - entscheidet ueber sprachspezifische App-Ids.
    lang() {
      return this.$store.getters['lang/getLang'] || this.$store.state.lang.active;
    },
    countInstalled() {
      return this.rows.filter((r) => r.action === 'installed').length;
    },
    countFailed() {
      return this.rows.filter((r) => r.action === 'failed').length;
    },
    countSkipped() {
      return this.rows.filter((r) => r.action === 'skipped').length;
    },
    // Kurzfassung ueber der Tabelle: was hat das Geraet zu den Zielen gesagt.
    summary() {
      if (!this.rows.length) return '';
      const by = (s) => this.rows.filter((r) => r.state === s).length;
      return (
        this.rows.length + ' Ziele: ' + by('installed') + ' installiert, ' +
        by('installable') + ' installierbar, ' + by('blocked') + ' gesperrt, ' +
        by('missing') + ' dem Geraet unbekannt, ' + by('unknown') + ' ohne Auskunft.'
      );
    },
    // Diagnose-Zeilen. level steuert nur die Farbe: ok / warn / bad / info.
    diagLines() {
      const c = this.caps;
      const lines = [];
      const yn = (v) => (v ? 'ja' : 'nein');
      if (!c) {
        lines.push({
          label: 'Geraete-API',
          value: 'keine Erkennung moeglich - dieses Geraet stellt keine Cast-API bereit',
          level: 'bad',
        });
        return lines;
      }
      if (Cast.platform === 'tizen') {
        lines.push({
          label: 'Tizen-Version',
          value: (c.platformVersion || 'unbekannt') + (c.supported ? '' : ' (< 6.5 - Cast nicht unterstuetzt)'),
          level: c.supported ? 'ok' : 'bad',
        });
        lines.push({
          label: 'webapis',
          value: yn(c.hasWebapis),
          level: c.hasWebapis ? 'ok' : 'bad',
        });
        lines.push({
          label: 'webapis.was (App-Store-API)',
          value:
            yn(c.hasWas) +
            (c.hasWas ? ' · getCapability: ' + yn(c.hasGetCapability) + ' · installApp: ' + yn(c.hasInstallApp) : ''),
          level: c.hasWas && c.hasGetCapability && c.hasInstallApp ? 'ok' : 'bad',
        });
        lines.push({
          label: 'SI-Token',
          value: c.tokenDetail || (c.tokenInitialized ? 'initialisiert' : 'nicht initialisiert'),
          level: c.tokenInitialized ? 'ok' : 'bad',
        });
        lines.push({
          label: 'App-Verzeichnis (tizen.application)',
          value: c.appList
            ? c.appList.length + ' Apps gelesen'
            : 'nicht lesbar - ' + (c.appListError || 'unbekannter Fehler'),
          level: c.appList ? 'ok' : 'warn',
        });
        lines.push({ label: 'Google Cast (webapis.googlecast)', value: yn(c.hasGoogleCast), level: c.hasGoogleCast ? 'ok' : 'warn' });
      } else if (Cast.platform === 'lg') {
        lines.push({ label: 'Erkennung', value: c.detected ? 'abgeschlossen' : 'offen', level: c.detected ? 'ok' : 'warn' });
        lines.push({
          label: 'CP-Katalog',
          value: c.catalog ? c.catalog.length + ' Eintraege' : 'nicht lesbar',
          level: c.catalog && c.catalog.length ? 'ok' : 'warn',
        });
        lines.push({
          label: 'Vorinstallierte Apps',
          value: c.preloadedApps ? c.preloadedApps.length + ' Eintraege' : 'nicht lesbar',
          level: c.preloadedApps && c.preloadedApps.length ? 'ok' : 'warn',
        });
        if (c.serviceCountry) lines.push({ label: 'Service-Country', value: String(c.serviceCountry), level: 'info' });
      }
      return lines;
    },
    // Ein Satz, der den haeufigsten Grund fuer eine leere Tabelle benennt.
    blockingHint() {
      const c = this.caps;
      if (!c || Cast.platform !== 'tizen') return '';
      if (!c.hasWebapis) {
        return 'Ohne webapis liefert der TV keinerlei App-Auskunft. Auf Hospitality-Geraeten fehlt die API, ' +
          'wenn die App nicht als B2B-/Hotel-App laeuft (falscher App-Typ bzw. Partner-Zertifikat).';
      }
      if (!c.hasWas) {
        return 'Diese Firmware kennt webapis.was nicht - Installation und Zustandsabfrage von OTT-Apps sind ' +
          'darueber nicht moeglich. Apps muessen ueber ein Clone-Profil (.sclone) ausgerollt werden.';
      }
      if (!c.tokenInitialized) {
        return 'Der SI-Token wurde nicht angenommen (' + (c.tokenDetail || '') + '). Ohne gueltigen Token ' +
          'antwortet getCapability nicht - der Token gehoert zum Samsung-Partnervertrag und ist geraete-/kettenspezifisch.';
      }
      if (!c.appList) {
        return 'Das App-Verzeichnis ist nicht lesbar (' + (c.appListError || '') + '). Fehlt das Privileg ' +
          'http://tizen.org/privilege/application.info in der config.xml, entfaellt die zweite Auskunftsquelle.';
      }
      return '';
    },
  },
  methods: {
    stateLabel(row) {
      const base = STATE_LABEL[row.state] || row.state;
      if (row.action === 'installed') return 'neu installiert';
      if (row.action === 'failed') return 'FEHLGESCHLAGEN';
      return base;
    },
    // Zustand aller App-Ziele einlesen, ohne etwas zu installieren.
    //
    // WICHTIG: vorher cast/prepare. Auf Samsung antwortet was.getCapability erst
    // nach initializeSIToken - ohne diesen Schritt meldet der TV zu JEDER App
    // "keine Auskunft", und die Seite sieht aus, als kenne das Geraet keine Apps.
    // cast/prepare ist idempotent.
    loadPlan() {
      this.loading = true;
      this.finished = false;
      this.caps = null;
      const device = (this.$store.state && this.$store.state.device) || {};
      return Promise.resolve()
        .then(() => this.$store.dispatch('cast/prepare'))
        .catch(() => null)
        // Ohne apps-Angabe: der volle Katalog. Die sichtbaren Kacheln sind seit
        // der Umstellung nur eine Teilmenge (Cast, Netflix, Spotify) - geprueft
        // werden soll aber jede App, die auf dem Geraet liegen sollte.
        .then(() => Cast.getInstallPlan({ lang: this.lang }))
        .then((plan) => {
          this.rows = plan;
          this.total = plan.filter((p) => p.state === 'installable').length;
          console.log('[AppInstall] Plan:', plan, 'device:', device.deviceId);
          return plan;
        })
        .catch((err) => {
          console.log('[AppInstall] Plan fehlgeschlagen:', err);
          this.rows = [];
          return [];
        })
        .then((plan) => {
          // Diagnose nach dem Plan lesen: detect() ist da durchgelaufen.
          this.caps = Cast.capabilities();
          this.loading = false;
          return plan;
        });
    },
    // Installiert alles, was als 'installable' gemeldet ist.
    runInstall() {
      if (this.running) return Promise.resolve();
      this.running = true;
      this.done = 0;
      const plan = this.rows.slice();
      this.total = plan.length;
      return Cast.installPlan(plan, (entry, index, total) => {
        this.done = index;
        this.total = total;
        // Zeile an derselben Position ersetzen, damit die Tabelle stabil bleibt.
        const at = this.rows.findIndex((r) => r.key === entry.key && r.group === entry.group);
        if (at !== -1) this.rows.splice(at, 1, entry);
        console.log(
          '[AppInstall] ' + entry.appId + ' -> ' + entry.action + ' (' + entry.state + ') ' + (entry.detail || '')
        );
      })
        .then((results) => {
          this.running = false;
          this.finished = true;
          console.log('[AppInstall] Ergebnis:', results);
          // Danach den echten Zustand nachlesen: erfolgreiche Installationen
          // sollen auch vom Geraet als installiert bestaetigt sein.
          return Cast.detect(true)
            .then((caps) => {
              this.caps = caps || Cast.capabilities();
              return caps;
            })
            .catch(() => null);
        })
        .catch((err) => {
          this.running = false;
          this.finished = true;
          console.log('[AppInstall] Installation fehlgeschlagen:', err);
        });
    },
    scrollBy(dir, page = false) {
      const el = this.$refs.scrollContainer;
      if (!el) return;
      el.scrollTop += dir * (page ? Math.round(el.clientHeight * 0.85) : 120);
    },
    eventHandler(key) {
      switch (key) {
        case KEYS.UP:
          this.scrollBy(-1);
          break;
        case KEYS.DOWN:
          this.scrollBy(1);
          break;
        case KEYS.CHANNEL_UP:
        case KEYS.PAGE_UP:
          this.scrollBy(-1, true);
          break;
        case KEYS.CHANNEL_DOWN:
        case KEYS.PAGE_DOWN:
          this.scrollBy(1, true);
          break;
        case KEYS.YELLOW:
          this.runInstall();
          break;
        case KEYS.GREEN:
          if (!this.running) this.loadPlan();
          break;
        case KEYS.RED:
        case KEYS.BACK:
        case KEYS.EXIT:
          // Waehrend einer laufenden Installation nicht verlassen - sonst laufen
          // die hcap/WAS-Callbacks ohne Ziel weiter.
          if (!this.running) this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
        default:
          break;
      }
    },
  },
  created() {
    this.loadPlan();
  },
};
</script>

<style scoped>
.app-install {
  background-color: #1b1b1b;
  color: #f1f1f1;
  height: 100%;
  overflow-y: auto;
  padding: 1%;
}
h2 {
  font-size: x-large;
  font-weight: 700;
  margin: 0 1rem 1rem;
}
.badge {
  background-color: #37474f;
  border-radius: 0.4em;
  display: inline-block;
  font-size: medium;
  margin-left: 1rem;
  padding: 0.1em 0.6em;
  vertical-align: middle;
}
.hint {
  margin: 0 1rem 1rem;
}
.diag {
  background-color: #232f34;
  border-radius: 0.4em;
  margin: 0 1rem 1rem;
  padding: 0.6rem 0.8rem;
}
.diag-title {
  font-weight: 700;
  margin-bottom: 0.4rem;
}
.diag-line {
  display: flex;
  gap: 0.5rem;
}
.diag-label {
  flex: 0 0 22rem;
  color: #b0bec5;
}
.diag-value {
  flex: 1;
  word-break: break-word;
}
.diag-note {
  border-top: 1px solid #455a64;
  color: #ffe08a;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}
.diag-ok .diag-value {
  color: #7ddf8f;
}
.diag-warn .diag-value {
  color: #ffe08a;
}
.diag-bad .diag-value {
  color: #ff9e9e;
}
.diag-info .diag-value {
  color: #b6e0ff;
}
table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}
th {
  border-bottom: 2px solid #f1f1f1;
  font-weight: 700;
  padding: 0.3rem 0.5rem;
  text-align: left;
}
td {
  padding: 0.25rem 0.5rem;
  vertical-align: top;
  word-break: break-all;
}
.row {
  border-bottom: 1px solid #444;
}
.col-name {
  width: 24%;
}
.col-id {
  width: 34%;
}
.col-state {
  width: 16%;
}
.col-detail {
  width: 26%;
}
.group,
.lang {
  color: #9e9e9e;
}
.resolved {
  color: #ffe08a;
}
.variants {
  color: #b6e0ff;
}
.state-installed {
  color: #7ddf8f;
}
.state-installable {
  color: #ffe08a;
}
.state-blocked,
.state-missing {
  color: #ff9e9e;
}
.state-unknown {
  color: #b6e0ff;
}
</style>



// WEBPACK FOOTER //
// src/pages/AppInstall.vue